import { useEffect, useRef } from 'react';
import {
  MAINTENANCE_CLEAR_EVENT_NAME,
  MAINTENANCE_EVENT_NAME,
} from './maintenanceBus';
import { fetchMaintenancePublicState } from './maintenancePageApi';

const IDLE_POLL_MS = 15_000;
const SCHEDULED_POLL_MS = 2_000;
const PROBE_INTERVAL_MS = 300;

async function maintenanceClientOwnsOrigin() {
  try {
    const response = await fetch(`/__maintenance_mode?ts=${Date.now()}`, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (response.ok && String(response.headers.get('content-type') || '').includes('application/json')) {
      const body = await response.json();
      if (body?.maintenanceClient === true) return true;
      if (body?.gateway === true && body?.maintenanceClient === false) return false;
    }
  } catch (_) {}

  // nginx/static fallback 또는 구버전 프론트에서도 실제 점검 HTML이 원점을 소유했는지 확인한다.
  try {
    const response = await fetch(`/?__npc_maintenance_probe=${Date.now()}`, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) return false;
    const text = await response.text();
    return /name=["']npc-maintenance-client["']/i.test(text)
      || /SYSTEM\s+MAINTENANCE/i.test(text) && /maintenance\.js/i.test(text);
  } catch (_) {
    return false;
  }
}

export default function MaintenanceSwapReloadBridge() {
  const pollTimerRef = useRef(null);
  const startTimerRef = useRef(null);
  const probeTimerRef = useRef(null);
  const stoppedRef = useRef(false);
  const maintenanceExpectedRef = useRef(false);
  const clockOffsetRef = useRef(0);

  useEffect(() => {
    stoppedRef.current = false;

    const clearStartTimer = () => {
      clearTimeout(startTimerRef.current);
      startTimerRef.current = null;
    };

    const clearProbeTimer = () => {
      clearTimeout(probeTimerRef.current);
      probeTimerRef.current = null;
    };

    const probeAndReload = async () => {
      if (stoppedRef.current || !maintenanceExpectedRef.current) return;
      if (await maintenanceClientOwnsOrigin()) {
        window.location.reload();
        return;
      }
      clearProbeTimer();
      probeTimerRef.current = setTimeout(probeAndReload, PROBE_INTERVAL_MS);
    };

    const ensureProbe = () => {
      clearProbeTimer();
      void probeAndReload();
    };

    const armStart = (state) => {
      clearStartTimer();

      const plan = state?.plan;
      const startMs = Date.parse(plan?.startsAt || '');
      if (!Number.isFinite(startMs)) {
        if (!state?.active) {
          maintenanceExpectedRef.current = false;
          clearProbeTimer();
        }
        return;
      }

      const controllerNow = Date.now() + Number(clockOffsetRef.current || 0);
      const remaining = startMs - controllerNow;

      if (state?.active || remaining <= 0) {
        maintenanceExpectedRef.current = true;
        ensureProbe();
        return;
      }

      maintenanceExpectedRef.current = false;
      clearProbeTimer();

      // 장시간 예약도 setTimeout 최대값에 걸리지 않게 최대 60초 단위로 다시 계산한다.
      const delay = Math.max(1, Math.min(remaining, 60_000));
      startTimerRef.current = setTimeout(() => armStart(state), delay);
    };

    const refresh = async () => {
      if (stoppedRef.current) return null;
      try {
        const receivedAt = Date.now();
        const state = await fetchMaintenancePublicState();
        // 새 점검 워크플로는 앱 내부에서 화면 전환하므로 Nginx 원본 스왑을 기다리지 않는다.
        if (Number(state?.plan?.workflowVersion) === 2) {
          maintenanceExpectedRef.current = false;
          clearProbeTimer();
          clearStartTimer();
          return state;
        }
        const controllerTime = Number(state?.controllerTime);
        clockOffsetRef.current = Number.isFinite(controllerTime) ? controllerTime - receivedAt : 0;

        if (!state?.active && !state?.scheduled) {
          maintenanceExpectedRef.current = false;
          clearProbeTimer();
        }

        armStart(state);
        return state;
      } catch (_) {
        // Controller가 잠시 끊겨도 이미 시작 시각을 넘겼다면 origin probe는 계속 유지한다.
        if (maintenanceExpectedRef.current) ensureProbe();
        return null;
      }
    };

    const schedulePoll = async (delay = 0) => {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = setTimeout(async () => {
        const state = await refresh();
        if (stoppedRef.current) return;
        const nextDelay = state?.scheduled || state?.active ? SCHEDULED_POLL_MS : IDLE_POLL_MS;
        schedulePoll(nextDelay);
      }, delay);
    };

    const onMaintenanceEvent = () => {
      void refresh();
    };
    const onResume = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const onOnline = () => {
      void refresh();
    };

    window.addEventListener(MAINTENANCE_EVENT_NAME, onMaintenanceEvent);
    window.addEventListener(MAINTENANCE_CLEAR_EVENT_NAME, onMaintenanceEvent);
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onResume);
    schedulePoll(0);

    return () => {
      stoppedRef.current = true;
      maintenanceExpectedRef.current = false;
      clearTimeout(pollTimerRef.current);
      clearStartTimer();
      clearProbeTimer();
      window.removeEventListener(MAINTENANCE_EVENT_NAME, onMaintenanceEvent);
      window.removeEventListener(MAINTENANCE_CLEAR_EVENT_NAME, onMaintenanceEvent);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onResume);
    };
  }, []);

  return null;
}
